/**
 * Bird Records API — simulated async data layer.
 *
 * In production, replace each function body with a real fetch() call to your
 * backend (e.g. Supabase, REST API, GraphQL).  The hook layer above is
 * completely decoupled from this file, so swapping implementations requires
 * no changes to components.
 */

import {
  MOCK_MOVEMENTS,
  MOCK_FLOCK_SUMMARIES,
  type BirdMovement,
  type FlockPopulationSummary,
  type BirdMovementStatus,
  type BirdMovementType,
} from '@/app/bird-records/components/birdRecordsData';

// ─── In-memory store (replaces DB in dev/demo) ────────────────────────────────

let _movements: BirdMovement[] = [...MOCK_MOVEMENTS];
let _flockSummaries: FlockPopulationSummary[] = [...MOCK_FLOCK_SUMMARIES];

// ─── Shared sale record type (mirrors BirdSalesContent local type) ────────────

export type BirdSaleStatus =
  | 'draft' |'pending_approval' |'confirmed' |'posted' |'rejected' |'cancelled';

export interface BirdSaleRecord {
  id: string;
  saleNumber: string;
  saleDate: string;
  farm: string;
  house: string;
  flock: string;
  flockNumber: string;
  birdType: string;
  breed: string;
  birdCategory: string;
  quantity: number;
  unitPrice: number;
  totalRevenue: number;
  currency: string;
  populationBefore?: number;
  populationAfter?: number;
  customerName: string;
  customerContact?: string;
  paymentMethod: string;
  paymentStatus: 'paid' | 'partial' | 'unpaid';
  amountPaid: number;
  balance: number;
  referenceInvoice?: string;
  deliveryMethod: 'farm_gate' | 'delivered' | 'collected';
  authorizedBy: string;
  recordedBy: string;
  approvedBy?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  notes?: string;
  status: BirdSaleStatus;
  movementId?: string;
  submittedAt?: string;
  confirmedAt?: string;
  postedAt?: string;
  createdAt: string;
}

// Seed sale records from the mock data embedded in BirdSalesContent
let _saleRecords: BirdSaleRecord[] = [
  {
    id: 'bsale-001', saleNumber: 'BSL-2026-000001', saleDate: '2026-08-20',
    farm: 'Idudi Poultry Farm', house: 'House A1', flock: 'Batch A1 — Cobb 500',
    flockNumber: 'BR-2026-001', birdType: 'Broiler', breed: 'Cobb 500', birdCategory: 'broiler',
    quantity: 500, unitPrice: 23000, totalRevenue: 11500000, currency: 'UGX',
    populationBefore: 4812, populationAfter: 4312,
    customerName: 'Jinja Poultry Traders', customerContact: '+256 782 777 888',
    paymentMethod: 'Mobile Money', paymentStatus: 'paid', amountPaid: 11500000, balance: 0,
    referenceInvoice: 'INV-2026-0039', deliveryMethod: 'collected',
    authorizedBy: 'Farm Manager', recordedBy: 'James Okello', approvedBy: 'Operations Director',
    notes: 'Full batch collected by buyer. Weighed at farm gate.',
    status: 'posted', movementId: 'BRM-2026-000012',
    submittedAt: '2026-08-20T08:00:00Z', confirmedAt: '2026-08-20T09:00:00Z',
    postedAt: '2026-08-20T09:10:00Z', createdAt: '2026-08-20T07:30:00Z',
  },
  {
    id: 'bsale-002', saleNumber: 'BSL-2026-000002', saleDate: '2026-09-01',
    farm: 'Idudi Poultry Farm', house: 'House A1', flock: 'Batch A1 — Cobb 500',
    flockNumber: 'BR-2026-001', birdType: 'Broiler', breed: 'Cobb 500', birdCategory: 'broiler',
    quantity: 120, unitPrice: 22000, totalRevenue: 2640000, currency: 'UGX',
    populationBefore: 4312, populationAfter: 4192,
    customerName: 'Kampala Fresh Markets Ltd', customerContact: '+256 772 111 222',
    paymentMethod: 'Mobile Money', paymentStatus: 'partial', amountPaid: 1320000, balance: 1320000,
    referenceInvoice: 'INV-2026-0041', deliveryMethod: 'delivered',
    authorizedBy: 'Farm Manager', recordedBy: 'James Okello', approvedBy: 'Operations Director',
    notes: 'Partial payment on delivery. Balance due 15 Sep.',
    status: 'posted', movementId: 'BRM-2026-000015',
    submittedAt: '2026-09-01T07:00:00Z', confirmedAt: '2026-09-01T08:00:00Z',
    postedAt: '2026-09-01T08:10:00Z', createdAt: '2026-09-01T06:30:00Z',
  },
  {
    id: 'bsale-003', saleNumber: 'BSL-2026-000003', saleDate: '2026-09-03',
    farm: 'Idudi Poultry Farm', house: 'Layer House 01', flock: 'Layers Batch L1 — Lohmann',
    flockNumber: 'LY-2026-001', birdType: 'Layer', breed: 'Lohmann Brown', birdCategory: 'layer',
    quantity: 50, unitPrice: 35000, totalRevenue: 1750000, currency: 'UGX',
    populationBefore: 7792, populationAfter: 7742,
    customerName: 'Mr. David Ssemakula', customerContact: '+256 756 555 666',
    paymentMethod: 'Cash', paymentStatus: 'partial', amountPaid: 1000000, balance: 750000,
    referenceInvoice: 'INV-2026-0037', deliveryMethod: 'farm_gate',
    authorizedBy: 'Farm Manager', recordedBy: 'Moses Wafula',
    notes: 'Point-of-lay hens. Balance to be paid on next delivery.',
    status: 'posted', movementId: 'BRM-2026-000016',
    submittedAt: '2026-09-03T10:00:00Z', confirmedAt: '2026-09-03T11:00:00Z',
    postedAt: '2026-09-03T11:05:00Z', createdAt: '2026-09-03T09:30:00Z',
  },
  {
    id: 'bsale-004', saleNumber: 'BSL-2026-000004', saleDate: '2026-09-04',
    farm: 'Kampala Demo Farm', house: 'House C1', flock: 'Batch C1 — Ross 308',
    flockNumber: 'BR-2026-004', birdType: 'Broiler', breed: 'Ross 308', birdCategory: 'broiler',
    quantity: 200, unitPrice: 24000, totalRevenue: 4800000, currency: 'UGX',
    populationBefore: 3100, populationAfter: 2900,
    customerName: 'Owino Supermarket', customerContact: '+256 701 333 444',
    paymentMethod: 'Bank Transfer', paymentStatus: 'unpaid', amountPaid: 0, balance: 4800000,
    deliveryMethod: 'delivered', authorizedBy: 'Farm Manager', recordedBy: 'Peter Ssemakula',
    status: 'confirmed',
    submittedAt: '2026-09-04T09:00:00Z', confirmedAt: '2026-09-04T10:00:00Z',
    createdAt: '2026-09-04T08:30:00Z',
  },
  {
    id: 'bsale-005', saleNumber: 'BSL-2026-000005', saleDate: '2026-09-05',
    farm: 'Idudi Poultry Farm', house: 'House B1', flock: 'Batch B1 — Ross 308',
    flockNumber: 'BR-2026-003', birdType: 'Broiler', breed: 'Ross 308', birdCategory: 'broiler',
    quantity: 300, unitPrice: 23500, totalRevenue: 7050000, currency: 'UGX',
    populationBefore: 3175, populationAfter: 2875,
    customerName: 'Entebbe Hotel & Suites', customerContact: '+256 414 999 000',
    paymentMethod: 'Bank Transfer', paymentStatus: 'unpaid', amountPaid: 0, balance: 7050000,
    deliveryMethod: 'delivered', authorizedBy: 'Operations Director', recordedBy: 'James Okello',
    notes: 'Awaiting approval before dispatch.',
    status: 'pending_approval',
    submittedAt: '2026-09-05T08:00:00Z', createdAt: '2026-09-05T07:30:00Z',
  },
  {
    id: 'bsale-006', saleNumber: 'BSL-2026-000006', saleDate: '2026-09-06',
    farm: 'Idudi Poultry Farm', house: 'Breeder House 01', flock: 'Breeders Batch BRD-001',
    flockNumber: 'BRD-2026-001', birdType: 'Breeder', breed: 'Ross Breeder', birdCategory: 'breeder',
    quantity: 80, unitPrice: 45000, totalRevenue: 3600000, currency: 'UGX',
    populationBefore: 1182, populationAfter: 1102,
    customerName: 'Ms. Grace Nakato', customerContact: '+256 703 121 314',
    paymentMethod: 'Cash', paymentStatus: 'unpaid', amountPaid: 0, balance: 3600000,
    deliveryMethod: 'farm_gate', authorizedBy: 'Farm Manager', recordedBy: 'Sarah Namukasa',
    status: 'draft', createdAt: '2026-09-06T06:00:00Z',
  },
];

// ─── Simulated network delay ──────────────────────────────────────────────────

const delay = (ms = 300) => new Promise<void>((res) => setTimeout(res, ms));

// ─── Bird Movements API ───────────────────────────────────────────────────────

export interface MovementsFilter {
  search?: string;
  type?: BirdMovementType | 'all';
  status?: BirdMovementStatus | 'all';
  direction?: 'in' | 'out' | 'neutral' | 'all';
  flockId?: string;
}

export async function fetchMovements(filters?: MovementsFilter): Promise<BirdMovement[]> {
  await delay();
  let result = [..._movements];
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (m) =>
        m.movementNumber.toLowerCase().includes(q) ||
        m.flockNumber.toLowerCase().includes(q) ||
        m.flockName.toLowerCase().includes(q) ||
        m.farmName.toLowerCase().includes(q),
    );
  }
  if (filters?.type && filters.type !== 'all') result = result.filter((m) => m.movementType === filters.type);
  if (filters?.status && filters.status !== 'all') result = result.filter((m) => m.status === filters.status);
  if (filters?.direction && filters.direction !== 'all') result = result.filter((m) => m.direction === filters.direction);
  if (filters?.flockId) result = result.filter((m) => m.flockId === filters.flockId);
  return result.sort((a, b) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime());
}

export async function createMovement(data: Omit<BirdMovement, 'id' | 'movementNumber' | 'createdAt' | 'updatedAt'>): Promise<BirdMovement> {
  await delay(400);
  const newMovement: BirdMovement = {
    ...data,
    id: `brm-${Date.now()}`,
    movementNumber: `BRM-2026-${String(_movements.length + 1).padStart(6, '0')}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  _movements = [newMovement, ..._movements];
  return newMovement;
}

export async function updateMovementStatus(
  id: string,
  status: BirdMovementStatus,
  updatedBy?: string,
): Promise<BirdMovement> {
  await delay(300);
  const idx = _movements.findIndex((m) => m.id === id);
  if (idx === -1) throw new Error(`Movement ${id} not found`);
  const updated: BirdMovement = {
    ..._movements[idx],
    status,
    approvedBy: status === 'posted' || status === 'approved' ? (updatedBy ?? _movements[idx].approvedBy) : _movements[idx].approvedBy,
    updatedAt: new Date().toISOString(),
  };
  _movements = _movements.map((m) => (m.id === id ? updated : m));
  return updated;
}

export async function deleteMovement(id: string): Promise<void> {
  await delay(200);
  _movements = _movements.filter((m) => m.id !== id);
}

// ─── Flock Population API ─────────────────────────────────────────────────────

export interface FlockPopulationFilter {
  search?: string;
  farm?: string;
  birdType?: string;
  status?: string;
}

export async function fetchFlockPopulation(filters?: FlockPopulationFilter): Promise<FlockPopulationSummary[]> {
  await delay();
  // Recompute current population from the live movement ledger
  const enriched = _flockSummaries.map((flock) => {
    const posted = _movements.filter((m) => m.flockId === flock.flockId && m.status === 'posted');
    if (posted.length === 0) return flock;
    const currentBirds = Math.max(
      0,
      posted.reduce((acc, m) => {
        if (m.direction === 'in') return acc + m.quantity;
        if (m.direction === 'out') return acc - m.quantity;
        return acc;
      }, 0),
    );
    const mortality = flock.initialBirds - currentBirds - flock.culled - flock.sold;
    const survivalRate = flock.initialBirds > 0 ? parseFloat(((currentBirds / flock.initialBirds) * 100).toFixed(2)) : 0;
    const mortalityRate = 100 - survivalRate;
    return { ...flock, currentBirds, mortality: Math.max(0, mortality), survivalRate, mortalityRate };
  });

  let result = enriched;
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (f) =>
        f.flockNumber.toLowerCase().includes(q) ||
        f.flockName.toLowerCase().includes(q) ||
        f.farm.toLowerCase().includes(q) ||
        f.house.toLowerCase().includes(q),
    );
  }
  if (filters?.farm && filters.farm !== 'all') result = result.filter((f) => f.farm === filters.farm);
  if (filters?.birdType && filters.birdType !== 'all') result = result.filter((f) => f.birdType === filters.birdType);
  if (filters?.status && filters.status !== 'all') result = result.filter((f) => f.status === filters.status);
  return result;
}

export async function updateFlockPopulation(
  flockId: string,
  patch: Partial<FlockPopulationSummary>,
): Promise<FlockPopulationSummary> {
  await delay(300);
  const idx = _flockSummaries.findIndex((f) => f.flockId === flockId);
  if (idx === -1) throw new Error(`Flock ${flockId} not found`);
  const updated = { ..._flockSummaries[idx], ...patch };
  _flockSummaries = _flockSummaries.map((f) => (f.flockId === flockId ? updated : f));
  return updated;
}

// ─── Sale Records API ─────────────────────────────────────────────────────────

export interface SaleRecordsFilter {
  search?: string;
  status?: BirdSaleStatus | 'all';
  farm?: string;
}

export async function fetchSaleRecords(filters?: SaleRecordsFilter): Promise<BirdSaleRecord[]> {
  await delay();
  let result = [..._saleRecords];
  if (filters?.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (s) =>
        s.saleNumber.toLowerCase().includes(q) ||
        s.customerName.toLowerCase().includes(q) ||
        s.flockNumber.toLowerCase().includes(q) ||
        s.farm.toLowerCase().includes(q),
    );
  }
  if (filters?.status && filters.status !== 'all') result = result.filter((s) => s.status === filters.status);
  if (filters?.farm && filters.farm !== 'all') result = result.filter((s) => s.farm === filters.farm);
  return result.sort((a, b) => new Date(b.saleDate).getTime() - new Date(a.saleDate).getTime());
}

export async function createSaleRecord(
  data: Omit<BirdSaleRecord, 'id' | 'saleNumber' | 'createdAt'>,
): Promise<BirdSaleRecord> {
  await delay(400);
  const newSale: BirdSaleRecord = {
    ...data,
    id: `bsale-${Date.now()}`,
    saleNumber: `BSL-2026-${String(_saleRecords.length + 1).padStart(6, '0')}`,
    createdAt: new Date().toISOString(),
  };
  _saleRecords = [newSale, ..._saleRecords];
  return newSale;
}

export async function updateSaleRecord(
  id: string,
  patch: Partial<BirdSaleRecord>,
): Promise<BirdSaleRecord> {
  await delay(300);
  const idx = _saleRecords.findIndex((s) => s.id === id);
  if (idx === -1) throw new Error(`Sale record ${id} not found`);
  const updated = { ..._saleRecords[idx], ...patch };
  _saleRecords = _saleRecords.map((s) => (s.id === id ? updated : s));

  // When a sale is posted, auto-create a population-OUT movement in the ledger
  if (patch.status === 'posted' && !_saleRecords[idx].movementId) {
    const sale = updated;
    const flockSummary = _flockSummaries.find((f) => f.flockNumber === sale.flockNumber);
    if (flockSummary) {
      const postedMovements = _movements.filter(
        (m) => m.flockId === flockSummary.flockId && m.status === 'posted',
      );
      const currentPop = postedMovements.reduce((acc, m) => {
        if (m.direction === 'in') return acc + m.quantity;
        if (m.direction === 'out') return acc - m.quantity;
        return acc;
      }, 0);
      const newMovement: BirdMovement = {
        id: `brm-sale-${Date.now()}`,
        organizationId: 'org-001',
        movementNumber: `BRM-2026-${String(_movements.length + 1).padStart(6, '0')}`,
        movementType: 'sale',
        movementDate: sale.saleDate,
        farmId: flockSummary.farm,
        farmName: sale.farm,
        houseId: flockSummary.house,
        houseName: sale.house,
        flockId: flockSummary.flockId,
        flockNumber: sale.flockNumber,
        flockName: sale.flock,
        birdType: sale.birdType,
        breed: sale.breed,
        quantity: sale.quantity,
        direction: 'out',
        populationBefore: currentPop,
        populationAfter: Math.max(0, currentPop - sale.quantity),
        referenceType: 'Sales Record',
        referenceId: sale.saleNumber,
        reason: `Sale to ${sale.customerName}`,
        status: 'posted',
        recordedBy: sale.recordedBy,
        approvedBy: sale.approvedBy,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      _movements = [newMovement, ..._movements];
      // Link movement back to sale record
      _saleRecords = _saleRecords.map((s) =>
        s.id === id ? { ...s, movementId: newMovement.movementNumber } : s,
      );
    }
  }

  return updated;
}

export async function deleteSaleRecord(id: string): Promise<void> {
  await delay(200);
  _saleRecords = _saleRecords.filter((s) => s.id !== id);
}
